import { axiosInstance } from "@retro-ia/shared/axios";
import type {
  ActivityRow,
  AnalyticsFiltersOptions,
  AnalyticsSummary,
  FinalClientRow,
  LearnerRow,
  ProgramRow,
  TableResponse,
} from "./types";

const buildParams = <T extends object>(params: T) => {
  const result: Record<string, unknown> = {};
  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      result[key] = value;
    }
  });
  return result;
};

export interface SummaryQueryParams {
  windowDays?: number;
  clienteIntermedioId?: number | null;
  clienteFinalId?: number | null;
  programaId?: number | null;
  actividadId?: number | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

export const fetchAnalyticsSummary = async (
  params: SummaryQueryParams = {}
) => {
  const {
    windowDays = 30,
    clienteIntermedioId,
    clienteFinalId,
    programaId,
    actividadId,
    dateFrom,
    dateTo,
  } = params;
  const { data } = await axiosInstance.get<AnalyticsSummary>(
    "/analytics/summary",
    {
      params: buildParams({
        window: windowDays,
        clienteIntermedioId,
        clienteFinalId,
        programaId,
        actividadId,
        dateFrom,
        dateTo,
      }),
    }
  );
  return data;
};

export interface ActivityTrendParams {
  days?: number;
  clienteIntermedioId?: number | null;
  clienteFinalId?: number | null;
  programaId?: number | null;
  actividadId?: number | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

export const fetchActivityTrend = async (params: ActivityTrendParams = {}) => {
  const {
    days = 30,
    clienteIntermedioId,
    clienteFinalId,
    programaId,
    actividadId,
    dateFrom,
    dateTo,
  } = params;
  const { data } = await axiosInstance.get<
    Array<{ fecha: string; intentos: number }>
  >("/analytics/activity/trend", {
    params: buildParams({
      days,
      clienteIntermedioId,
      clienteFinalId,
      programaId,
      actividadId,
      dateFrom,
      dateTo,
    }),
  });
  return data;
};

export interface ProgramsProgressParams {
  clienteIntermedioId?: number | null;
  clienteFinalId?: number | null;
  programaId?: number | null;
  actividadId?: number | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

export const fetchProgramsProgress = async (
  params: ProgramsProgressParams = {}
) => {
  const {
    clienteIntermedioId,
    clienteFinalId,
    programaId,
    actividadId,
    dateFrom,
    dateTo,
  } = params;
  const { data } = await axiosInstance.get<
    Array<{
      id_programa: number;
      nombre: string;
      avgProgreso: number;
      tasaFin: number;
    }>
  >("/analytics/programs/progress", {
    params: buildParams({
      onlyActive: true,
      clienteIntermedioId,
      clienteFinalId,
      programaId,
      actividadId,
      dateFrom,
      dateTo,
    }),
  });
  return data;
};

export interface NpsByClientParams {
  windowDays?: number;
  clienteIntermedioId?: number | null;
  clienteFinalId?: number | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

export const fetchNpsByClient = async (params: NpsByClientParams = {}) => {
  const {
    windowDays = 30,
    clienteIntermedioId,
    clienteFinalId,
    dateFrom,
    dateTo,
  } = params;
  const { data } = await axiosInstance.get<
    Array<{
      id_cliente_final: number;
      nombre_empresa: string;
      nps: number;
      total: number;
    }>
  >("/analytics/nps/by-client", {
    params: buildParams({
      window: windowDays,
      clienteIntermedioId,
      clienteFinalId,
      dateFrom,
      dateTo,
    }),
  });
  return data;
};

export const fetchTopClients = async () => {
  const { data } = await axiosInstance.get<
    Array<{ id_cliente_final: number; nombre_empresa: string; activos: number }>
  >("/analytics/clients/top", {
    params: { window: 7 },
  });
  return data;
};

export const fetchLearnersStalled = async () => {
  const { data } = await axiosInstance.get<
    Array<{
      row_key: string;
      id_aprendiz: number;
      nombre_empresa: string;
      ultima_actividad: string | null;
      progreso: number;
    }>
  >("/analytics/learners/stalled", {
    params: { days: 7, progressLt: 50, limit: 5 },
  });
  return data;
};

export const fetchDocumentIssues = async () => {
  const { data } = await axiosInstance.get<
    Array<{
      id: number;
      id_programa: number;
      mensaje: string;
      creado_el: string;
    }>
  >("/analytics/issues/documents", {
    params: { window: 7 },
  });
  return data;
};

export const fetchAnalyticsFilters = async () => {
  const { data } = await axiosInstance.get<AnalyticsFiltersOptions>(
    "/analytics/filters"
  );
  return data;
};

export interface FinalClientsQuery {
  page?: number;
  limit?: number;
  search?: string;
  clienteIntermedioId?: number | null;
  clienteFinalId?: number | null;
  programaId?: number | null;
  actividadId?: number | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

export const fetchFinalClientsTable = async (
  params: FinalClientsQuery
): Promise<TableResponse<FinalClientRow>> => {
  const { data } = await axiosInstance.get<TableResponse<FinalClientRow>>(
    "/analytics/tables/final-clients",
    { params: buildParams(params) }
  );
  return data;
};

export interface ProgramsTableQuery {
  page?: number;
  limit?: number;
  search?: string;
  clienteIntermedioId?: number | null;
  clienteFinalId?: number | null;
  programaId?: number | null;
  actividadId?: number | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

export const fetchProgramsTable = async (
  params: ProgramsTableQuery
): Promise<TableResponse<ProgramRow>> => {
  const { data } = await axiosInstance.get<TableResponse<ProgramRow>>(
    "/analytics/tables/programs",
    { params: buildParams(params) }
  );
  return data;
};

export interface ActivitiesTableQuery {
  page?: number;
  limit?: number;
  search?: string;
  clienteIntermedioId?: number | null;
  clienteFinalId?: number | null;
  programaId?: number | null;
  actividadId?: number | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

export const fetchActivitiesTable = async (
  params: ActivitiesTableQuery
): Promise<TableResponse<ActivityRow>> => {
  const { data } = await axiosInstance.get<TableResponse<ActivityRow>>(
    "/analytics/tables/activities",
    { params: buildParams(params) }
  );
  return data;
};

export interface LearnersTableQuery extends ActivitiesTableQuery {
  actividadId?: number | null;
}

export const fetchLearnersTable = async (
  params: LearnersTableQuery
): Promise<TableResponse<LearnerRow>> => {
  const { data } = await axiosInstance.get<TableResponse<LearnerRow>>(
    "/analytics/tables/learners",
    { params: buildParams(params) }
  );
  return data;
};

export interface ExportPdfQueryParams {
  clienteIntermedioId?: number | null;
  clienteFinalId?: number | null;
  programaId?: number | null;
  actividadId?: number | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

export const fetchExportDashboardPdf = async (
  params: ExportPdfQueryParams = {}
): Promise<Blob> => {
  const { data } = await axiosInstance.get<Blob>("/analytics/export/pdf", {
    params: buildParams(params),
    responseType: "blob",
  });
  return data;
};
